import ExternalLinkField from "components/links/ExternalLinkField";
import { Link } from "react-router-dom";
import { resolveRepairReference } from "utils/reparacionDte";

function formatDate(value) {
  if (value == null || value === "") return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-CL", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function formatMoney(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(n);
}

function display(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "—";
    }
  }
  return String(value);
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    return value;
  }
  return null;
}

function normalizeReferenceRows(item = {}) {
  const rows = [];
  const refs = Array.isArray(item.referencias) ? item.referencias : [];
  rows.push(...refs);

  const pn = item.payload_normalized || {};
  const po = item.payload_original || {};
  const pushIfObject = (value) => {
    if (value && typeof value === "object" && !Array.isArray(value)) rows.push(value);
  };
  const pushIfArray = (value) => {
    if (Array.isArray(value)) rows.push(...value);
  };

  pushIfObject(pn.referenciaDocumento);
  pushIfArray(pn.referencias);
  pushIfArray(pn.referenciasAdicionales);
  pushIfObject(pn.notaCredito?.referenciaDocumento);
  pushIfArray(pn.notaCredito?.referenciasAdicionales);
  pushIfObject(pn.notaDebito?.referenciaDocumento);
  pushIfArray(pn.notaDebito?.referenciasAdicionales);

  pushIfObject(po.referenciaDocumento);
  pushIfArray(po.referencias);
  pushIfArray(po.referenciasAdicionales);
  pushIfObject(po.notaCredito?.referenciaDocumento);
  pushIfArray(po.notaCredito?.referenciasAdicionales);
  pushIfObject(po.notaDebito?.referenciaDocumento);
  pushIfArray(po.notaDebito?.referenciasAdicionales);

  return rows
    .map((ref = {}, idx) => ({
      nroLinRef: firstNonEmpty(ref.NroLinRef, ref.nroLinRef, idx + 1),
      tpoDocRef: firstNonEmpty(ref.TpoDocRef, ref.tipoDocumento, ref.tpoDocRef, ref.tipo_doc_ref),
      folioRef: firstNonEmpty(ref.FolioRef, ref.folioRef, ref.folio, ref.folio_ref),
      fchRef: firstNonEmpty(ref.FchRef, ref.fchRef, ref.fecha, ref.fecha_ref),
      razonRef: firstNonEmpty(ref.RazonRef, ref.razonRef, ref.razonReferencia, ref.razon, ref.glosa),
      codRef: firstNonEmpty(ref.CodRef, ref.codRef, ref.codigo, ref.code),
      raw: ref,
    }))
    .filter((ref) =>
      ref.tpoDocRef != null
      || ref.folioRef != null
      || ref.fchRef != null
      || ref.razonRef != null
      || ref.codRef != null,
    );
}

/**
 * Si `detalle_items` (u otra fuente) trae líneas incompletas pero existe
 * `payload_original.items` (o `payload_normalized.items`), se mezclan por índice
 * para rellenar nombre, precio, unidad, etc., sin perder lo que ya viene de BD/SII.
 */
function enrichDetalleLines(item, lines) {
  if (!Array.isArray(lines) || lines.length === 0) return lines;

  const po = item.payload_original;
  const pn = item.payload_normalized;

  return lines.map((line, i) => {
    const poLine =
      Array.isArray(po?.items) &&
      po.items[i] &&
      typeof po.items[i] === "object"
        ? po.items[i]
        : {};
    const pnLine =
      Array.isArray(pn?.items) &&
      pn.items[i] &&
      typeof pn.items[i] === "object"
        ? pn.items[i]
        : {};
    const stored = line && typeof line === "object" ? line : {};
    return { ...poLine, ...pnLine, ...stored };
  });
}

/**
 * Prioridad: `detalle_items` → `payload_normalized` → `payload_original`
 * (items / detalle del request original). Las líneas se enriquecen con
 * `payload_original.items` / `payload_normalized.items` por índice cuando existan.
 */
export function resolveDetalleItems(item) {
  if (!item || typeof item !== "object") return [];

  let lines = [];

  const direct = item.detalle_items;
  if (Array.isArray(direct) && direct.length > 0) {
    lines = direct;
  } else {
    const pn = item.payload_normalized;
    if (pn && typeof pn === "object") {
      const candidates = [
        pn.detalle,
        pn.detalle_items,
        pn.items,
        pn.factura?.detalle,
        pn.boleta?.detalle,
        pn.notaCredito?.detalle,
        pn.notaDebito?.detalle,
        pn.guiaDespacho?.detalle,
      ];
      for (const c of candidates) {
        if (Array.isArray(c) && c.length > 0) {
          lines = c;
          break;
        }
      }
    }

    if (lines.length === 0) {
      const po = item.payload_original;
      if (po && typeof po === "object") {
        const fromOriginal = [
          po.items,
          po.detalle,
          po.detalle_items,
          po.factura?.detalle,
          po.invoice?.items,
          po.invoice?.detalle,
          po.boleta?.detalle,
          po.notaCredito?.detalle,
          po.notaDebito?.detalle,
          po.guiaDespacho?.detalle,
        ];
        for (const c of fromOriginal) {
          if (Array.isArray(c) && c.length > 0) {
            lines = c;
            break;
          }
        }
      }
    }
  }

  return enrichDetalleLines(item, lines);
}

function formatCdgItem(cdg) {
  if (cdg == null) return "";
  if (typeof cdg === "string") return cdg;
  const t = cdg.TpoCodigo ?? cdg.tipo ?? cdg.tpoCodigo;
  const v = cdg.VlrCodigo ?? cdg.valor ?? cdg.vlrCodigo;
  if (t != null && v != null) return `${t}: ${v}`;
  return display(cdg);
}

function lineLabel(line) {
  const name = line.NmbItem ?? line.nombre ?? line.nombreItem ?? "";
  const dsc = line.DscItem ?? line.descripcion ?? "";
  const title =
    name || dsc ? String(name || dsc) : "—";
  const parts = [];
  if (name && dsc && String(dsc) !== String(name)) parts.push(String(dsc));
  if (line.exento === true) parts.push("Exento");
  else if (line.tasaIva != null && line.tasaIva !== "") {
    parts.push(
      `IVA ${line.tasaIva}%${line.ivaIncluido ? " incl." : ""}`
    );
  }
  const subtitle = parts.length ? parts.join(" · ") : null;
  return { title, subtitle };
}

function lineNumber(line, index) {
  const n = line.NroLinDet ?? line.nroLinDet ?? line.linea;
  if (n != null && n !== "") return String(n);
  return String(index + 1);
}

/** Tabla de líneas de detalle (estructura SII / API interna). */
function DteDetalleItemsTable({ lines }) {
  if (!lines.length) {
    return (
      <p className="rounded-lg border border-dashed border-gray-200 bg-lightPrimary/80 px-4 py-6 text-center text-sm text-gray-500 dark:border-white/15 dark:bg-navy-900/50 dark:text-gray-400">
        No hay ítems en <code className="text-xs">detalle_items</code>,{" "}
        <code className="text-xs">payload_normalized</code> ni{" "}
        <code className="text-xs">payload_original</code> (p. ej.{" "}
        <code className="text-xs">items</code>).
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-white/10">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-lightPrimary/90 dark:border-white/10 dark:bg-navy-900/80">
            <th className="whitespace-nowrap px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-300">
              #
            </th>
            <th className="min-w-[200px] px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-300">
              Ítem / descripción
            </th>
            <th className="whitespace-nowrap px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-300">
              Código
            </th>
            <th className="whitespace-nowrap px-3 py-2.5 text-right text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-300">
              Cant.
            </th>
            <th className="whitespace-nowrap px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-300">
              U.
            </th>
            <th className="whitespace-nowrap px-3 py-2.5 text-right text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-300">
              P. unit.
            </th>
            <th className="whitespace-nowrap px-3 py-2.5 text-right text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-300">
              Monto
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => {
            const { title, subtitle } = lineLabel(line);
            const qty =
              line.QtyItem ?? line.qtyItem ?? line.cantidad ?? line.qty;
            const un = line.UnmdItem ?? line.unmdItem ?? line.unidad ?? "";
            const prc =
              line.PrcItem ??
              line.prcItem ??
              line.precioUnitario ??
              line.precio;
            const montoRaw =
              line.MontoItem ?? line.montoItem ?? line.monto ?? line.subtotal;
            let monto = montoRaw;
            if (
              (monto == null || monto === "") &&
              prc != null &&
              prc !== "" &&
              qty != null &&
              qty !== ""
            ) {
              const p = Number(prc);
              const q = Number(qty);
              if (!Number.isNaN(p) && !Number.isNaN(q)) {
                monto = p * q;
              }
            }
            const cdg = line.CdgItem ?? line.cdgItem ?? null;
            const codigo =
              formatCdgItem(cdg) ||
              (line.sku != null && String(line.sku).trim() !== ""
                ? String(line.sku).trim()
                : "");

            return (
              <tr
                key={`${lineNumber(line, index)}-${index}`}
                className="border-b border-gray-100 last:border-0 dark:border-white/5"
              >
                <td className="whitespace-nowrap px-3 py-2.5 align-top font-medium text-navy-800 dark:text-gray-100">
                  {lineNumber(line, index)}
                </td>
                <td className="max-w-md px-3 py-2.5 align-top text-navy-800 dark:text-gray-100">
                  <div className="font-medium leading-snug">{title}</div>
                  {subtitle ? (
                    <div className="mt-0.5 text-xs leading-snug text-gray-500 dark:text-gray-400">
                      {subtitle}
                    </div>
                  ) : null}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 align-top text-xs text-gray-600 dark:text-gray-400">
                  {codigo || "—"}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right align-top tabular-nums text-navy-800 dark:text-gray-100">
                  {qty != null && qty !== "" ? display(qty) : "—"}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 align-top text-navy-800 dark:text-gray-100">
                  {un ? String(un) : "—"}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right align-top tabular-nums text-navy-800 dark:text-gray-100">
                  {prc != null && prc !== "" ? formatMoney(prc) ?? display(prc) : "—"}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right align-top font-medium tabular-nums text-navy-800 dark:text-gray-100">
                  {monto != null && monto !== ""
                    ? formatMoney(monto) ?? display(monto)
                    : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Un documento reparado no se corrige: se emite uno nuevo y el viejo queda
// marcado. Abrir el viejo y ver solo su XML y su PDF —los del documento que
// justamente NO sirve— no decía nada de cuál lo reemplazó. Este aviso nombra
// al otro documento y lleva hasta él.
function AvisoReparacion({ item, onOpenDocument }) {
  const ref = resolveRepairReference(item);
  if (!ref) return null;

  // `kind` y no `label`: la etiqueta es texto de pantalla y cambiarla no debe
  // invertir el sentido del aviso.
  const esElReemplazo = ref.kind === "reemplaza_a";
  const titulo = esElReemplazo
    ? "Este documento reemplaza a otro"
    : "Este documento fue reemplazado";
  const explicacion = esElReemplazo
    ? "Se emitió para reparar el documento de abajo. El válido es este."
    : "Se reparó emitiendo el documento de abajo. El válido es ese, no este.";

  const partes = [
    ref.tipoDte != null ? `DTE ${ref.tipoDte}` : null,
    ref.folio != null ? `Folio ${ref.folio}` : null,
  ].filter(Boolean);
  const destino = ref.id ?? ref.uuid;

  return (
    <section className="rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-500/40 dark:bg-amber-500/10">
      <h3 className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
        {titulo}
      </h3>
      <p className="mt-1 text-sm text-amber-900 dark:text-amber-100">{explicacion}</p>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-sm font-semibold text-navy-800 dark:text-gray-100">
          {partes.length ? partes.join(" · ") : "Datos no disponibles"}
        </span>
        {destino != null && onOpenDocument ? (
          <button
            type="button"
            onClick={() => onOpenDocument(destino)}
            className="text-sm font-semibold text-brand-600 underline-offset-2 hover:underline dark:text-brand-300"
          >
            Ver documento #{destino}
          </button>
        ) : destino != null ? (
          <span className="text-sm text-navy-700 dark:text-gray-200">#{destino}</span>
        ) : null}
      </div>
    </section>
  );
}

function Section({ title, children, id }) {
  return (
    <section
      id={id}
      className="scroll-mt-28 rounded-xl border border-gray-200 bg-white/80 p-4 shadow-sm dark:border-white/10 dark:bg-navy-900/40"
    >
      <h3 className="mb-3 border-b border-gray-100 pb-2 text-xs font-bold uppercase tracking-wider text-brand-600 dark:border-white/10 dark:text-tertiary-400">
        {title}
      </h3>
      {children}
    </section>
  );
}

function FieldGrid({ rows }) {
  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
      {rows.map(({ label, value }) => (
        <div key={label} className="min-w-0">
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {label}
          </dt>
          <dd className="mt-0.5 break-words text-sm text-navy-800 dark:text-gray-100">
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Anclas para navegación dentro del modal de detalle DTE */
export const DTE_DETAIL_SECTION_IDS = {
  identificacion: "dte-detail-identificacion",
  emisor: "dte-detail-emisor",
  montos: "dte-detail-montos",
  sii: "dte-detail-sii",
  entrega: "dte-detail-entrega",
  archivos: "dte-detail-archivos",
  trazabilidad: "dte-detail-trazabilidad",
  items: "dte-detail-items",
  referencias: "dte-detail-referencias",
  json: "dte-detail-json",
};

function scrollToDetailSection(sectionId) {
  const el = document.getElementById(sectionId);
  if (!el) return;
  if (el instanceof HTMLDetailsElement) {
    el.open = true;
  }
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function DteDetailSectionNav({ hasReferencias }) {
  const entries = [
    { id: DTE_DETAIL_SECTION_IDS.identificacion, label: "Identificación" },
    { id: DTE_DETAIL_SECTION_IDS.emisor, label: "Emisor y receptor" },
    { id: DTE_DETAIL_SECTION_IDS.montos, label: "Montos" },
    { id: DTE_DETAIL_SECTION_IDS.sii, label: "SII y seguimiento" },
    { id: DTE_DETAIL_SECTION_IDS.entrega, label: "Entrega" },
    { id: DTE_DETAIL_SECTION_IDS.archivos, label: "Archivos" },
    { id: DTE_DETAIL_SECTION_IDS.trazabilidad, label: "Trazabilidad" },
    { id: DTE_DETAIL_SECTION_IDS.items, label: "Ítems" },
    ...(hasReferencias
      ? [{ id: DTE_DETAIL_SECTION_IDS.referencias, label: "Referencias" }]
      : []),
    { id: DTE_DETAIL_SECTION_IDS.json, label: "JSON" },
  ];

  return (
    <nav
      className="sticky top-0 z-[2] -mx-1 mb-1 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-3 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-navy-800/95"
      aria-label="Secciones del documento DTE"
    >
      <p className="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Ir a sección
      </p>
      <div className="flex flex-wrap gap-2">
        {entries.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => scrollToDetailSection(id)}
            className="rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 text-left text-xs font-medium text-brand-700 transition hover:border-brand-400 hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-brand-400/50 dark:border-brand-500/40 dark:bg-navy-900/80 dark:text-tertiary-300 dark:hover:border-tertiary-400 dark:hover:bg-navy-800"
          >
            {label}
          </button>
        ))}
      </div>
    </nav>
  );
}

export function DteDocumentDetailFields({ item, onOpenDocument }) {
  if (!item) return null;

  const detalleLines = resolveDetalleItems(item);
  const refs = normalizeReferenceRows(item);

  const identification = [
    { label: "ID interno", value: display(item.id) },
    { label: "UUID documento", value: display(item.document_uuid) },
    { label: "Internal ID", value: display(item.internal_id) },
    { label: "GID", value: display(item.gid) },
    { label: "Tipo DTE", value: display(item.tipo_dte) },
    { label: "Folio", value: display(item.folio) },
    { label: "Modo", value: display(item.modo) },
    { label: "Tipo documento", value: display(item.document_kind) },
    {
      label: "Estado ciclo",
      value: item.lifecycle_status === "accepted_with_observations"
        ? "Aceptado con reparos"
        : display(item.lifecycle_status),
    },
    { label: "Track ID (SII)", value: display(item.sii_track_id) },
  ];

  const parties = [
    { label: "RUT emisor", value: display(item.rut_emisor) },
    { label: "RUT receptor", value: display(item.rut_receptor) },
    { label: "Receptor (razón social)", value: display(item.receptor_razon_social) },
    { label: "Fecha emisión", value: formatDate(item.fch_emis) ?? display(item.fch_emis) },
  ];

  const amounts = [
    { label: "Monto exento", value: formatMoney(item.monto_exento) ?? "—" },
    { label: "Monto neto", value: formatMoney(item.monto_neto) ?? "—" },
    { label: "Monto IVA", value: formatMoney(item.monto_iva) ?? "—" },
    { label: "Monto total", value: formatMoney(item.monto_total) ?? "—" },
  ];

  const sii = [
    { label: "Estado SII", value: display(item.sii_estado) },
    { label: "Glosa SII", value: display(item.sii_glosa) },
    { label: "Envío SII", value: display(item.sii_upload_status) },
    { label: "HTTP envío", value: display(item.sii_upload_http_status) },
    { label: "Aceptado SII", value: formatDate(item.sii_accepted_at) ?? "—" },
    { label: "Rechazado SII", value: formatDate(item.sii_rejected_at) ?? "—" },
    { label: "Última consulta SII", value: formatDate(item.sii_last_checked_at) ?? "—" },
    { label: "Intentos tracking", value: display(item.tracking_attempt) },
    { label: "Error tracking", value: display(item.tracking_last_error) },
  ];

  const delivery = [
    { label: "Para (email)", value: display(item.delivery_to) },
    { label: "CC", value: display(item.delivery_cc) },
    { label: "Estado envío", value: display(item.delivery_status) },
    { label: "Proveedor", value: display(item.delivery_provider) },
    { label: "Enviado", value: formatDate(item.delivery_sent_at) ?? "—" },
    { label: "Intentos", value: display(item.delivery_attempt) },
    { label: "Último intento", value: formatDate(item.delivery_last_attempt_at) ?? "—" },
    { label: "Error envío", value: display(item.delivery_error) },
    { label: "Message ID", value: display(item.delivery_message_id) },
  ];

  const trace = [
    // Pasarela de pago del canal (Shopify / MercadoLibre / Falabella). Es la
    // misma que se imprime en el PDF. No es el FmaPago del XML.
    { label: "Método de pago", value: display(item.notes?.metodo_pago_canal) },
    { label: "Sistema origen", value: display(item.source_system) },
    { label: "ID externo", value: display(item.source_external_id) },
    { label: "Idempotency key", value: display(item.idempotency_key) },
    { label: "Endpoint origen", value: display(item.source_endpoint) },
    { label: "Creado", value: formatDate(item.created_at) ?? "—" },
    { label: "Actualizado", value: formatDate(item.updated_at) ?? "—" },
  ];

  return (
    <div className="space-y-5">
      <DteDetailSectionNav hasReferencias={refs.length > 0} />

      {/* Antes de todo lo demás: si este documento no es el válido, eso es lo
          primero que hay que saber al abrirlo. */}
      <AvisoReparacion item={item} onOpenDocument={onOpenDocument} />

      <Section
        id={DTE_DETAIL_SECTION_IDS.identificacion}
        title="Identificación"
      >
        <FieldGrid rows={identification} />
      </Section>

      <Section id={DTE_DETAIL_SECTION_IDS.emisor} title="Emisor y receptor">
        <FieldGrid rows={parties} />
      </Section>

      <Section id={DTE_DETAIL_SECTION_IDS.montos} title="Montos">
        <FieldGrid rows={amounts} />
      </Section>

      <Section id={DTE_DETAIL_SECTION_IDS.sii} title="SII y seguimiento">
        <FieldGrid rows={sii} />
      </Section>

      <Section id={DTE_DETAIL_SECTION_IDS.entrega} title="Entrega (email)">
        <FieldGrid rows={delivery} />
      </Section>

      <Section id={DTE_DETAIL_SECTION_IDS.archivos} title="Archivos y enlaces">
        <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
          <ExternalLinkField fieldLabel="PDF" url={item.pdf_url} />
          <ExternalLinkField fieldLabel="XML DTE (URL)" url={item.dte_xml_url} />
          <ExternalLinkField
            fieldLabel="XML envío (URL)"
            url={item.envio_xml_url}
          />
          <ExternalLinkField fieldLabel="Timbre PNG" url={item.timbre_png_url} />
        </div>
      </Section>

      <Section id={DTE_DETAIL_SECTION_IDS.trazabilidad} title="Trazabilidad">
        <FieldGrid rows={trace} />
      </Section>

      <section
        id={DTE_DETAIL_SECTION_IDS.items}
        className="scroll-mt-28 rounded-xl border border-gray-200 bg-white/80 p-4 shadow-sm dark:border-white/10 dark:bg-navy-900/40"
      >
        <h3 className="mb-1 border-b border-gray-100 pb-2 text-xs font-bold uppercase tracking-wider text-brand-600 dark:border-white/10 dark:text-tertiary-400">
          Ítems del documento
        </h3>
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          Orden de fuente: <code className="text-[10px]">detalle_items</code>, si
          no hay filas se usa el detalle en{" "}
          <code className="text-[10px]">payload_normalized</code> o{" "}
          <code className="text-[10px]">payload_original</code>. Si hay filas
          parciales en BD, se completan con{" "}
          <code className="text-[10px]">payload_original.items</code> /{" "}
          <code className="text-[10px]">payload_normalized.items</code> por fila.
          Total: {detalleLines.length}{" "}
          {detalleLines.length === 1 ? "ítem" : "ítems"}.
        </p>
        <DteDetalleItemsTable lines={detalleLines} />
      </section>

      {refs.length > 0 ? (
        <section
          id={DTE_DETAIL_SECTION_IDS.referencias}
          className="scroll-mt-28 rounded-xl border border-gray-200 bg-white/80 p-4 shadow-sm dark:border-white/10 dark:bg-navy-900/40"
        >
          <h3 className="mb-3 border-b border-gray-100 pb-2 text-xs font-bold uppercase tracking-wider text-brand-600 dark:border-white/10 dark:text-tertiary-400">
            Referencias
          </h3>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-white/10">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-lightPrimary/90 dark:border-white/10 dark:bg-navy-900/80">
                  <th className="px-3 py-2 text-xs font-bold uppercase text-gray-600 dark:text-gray-300">
                    #
                  </th>
                  <th className="px-3 py-2 text-xs font-bold uppercase text-gray-600 dark:text-gray-300">
                    Tipo doc.
                  </th>
                  <th className="px-3 py-2 text-xs font-bold uppercase text-gray-600 dark:text-gray-300">
                    Folio
                  </th>
                  <th className="px-3 py-2 text-xs font-bold uppercase text-gray-600 dark:text-gray-300">
                    Fecha
                  </th>
                  <th className="min-w-[180px] px-3 py-2 text-xs font-bold uppercase text-gray-600 dark:text-gray-300">
                    Razón / nota
                  </th>
                  <th className="min-w-[140px] px-3 py-2 text-xs font-bold uppercase text-gray-600 dark:text-gray-300">
                    Vinculado
                  </th>
                </tr>
              </thead>
              <tbody>
                {refs.map((ref, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-gray-100 last:border-0 dark:border-white/5"
                  >
                    <td className="px-3 py-2 align-top tabular-nums">
                      {display(ref.nroLinRef ?? idx + 1)}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {display(ref.tpoDocRef)}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {display(ref.folioRef)}
                    </td>
                    <td className="px-3 py-2 align-top whitespace-nowrap">
                      {display(ref.fchRef)}
                    </td>
                    <td className="px-3 py-2 align-top text-xs text-gray-600 dark:text-gray-400">
                      {display(ref.razonRef)}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {ref.tpoDocRef != null && ref.folioRef != null ? (
                        <Link
                          to={`/admin/data-tables?${new URLSearchParams({
                            tipo_dte: String(ref.tpoDocRef),
                            q: String(ref.folioRef),
                            ...(item.modo ? { modo: String(item.modo) } : {}),
                            ...(item.rut_emisor ? { rut_emisor: String(item.rut_emisor) } : {}),
                          }).toString()}`}
                          className="inline-flex rounded-md border border-brand-200 bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-700 transition hover:border-brand-400 hover:bg-brand-100 dark:border-brand-500/40 dark:bg-brand-500/10 dark:text-brand-200 dark:hover:bg-brand-500/20"
                        >
                          Ver documento
                        </Link>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <details
        id={DTE_DETAIL_SECTION_IDS.json}
        className="scroll-mt-28 rounded-lg border border-gray-200 bg-lightPrimary p-3 dark:border-white/10 dark:bg-navy-900"
      >
        <summary className="cursor-pointer text-xs font-semibold text-gray-600 dark:text-gray-300">
          Respuesta JSON completa
        </summary>
        <pre className="mt-2 max-h-48 overflow-auto text-[11px] leading-snug text-navy-800 dark:text-gray-300">
          {JSON.stringify(item, null, 2)}
        </pre>
      </details>
    </div>
  );
}
